import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns/format';
import { TriangleAlert } from 'lucide-react';

import { useTableDataQuery } from '@/modules/common';
import { PaginatedTable, TableItemStatus } from '@/modules/common/components';

import { Event } from '../../types';

interface EventsTableProps {
  onClick: (item: Event) => void;
}

const columns: ColumnDef<Event>[] = [
  {
    size: 300,
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => row.getValue('name'),
  },
  {
    size: 200,
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <TableItemStatus status={row.getValue('status')} />,
  },
  {
    size: 200,
    accessorKey: 'startDate',
    header: 'Start date',
    cell: ({ row }) =>
      format(new Date(row.getValue('startDate')), 'yyyy-MM-dd'),
  },
  {
    size: 300,
    accessorKey: 'validationErrors',
    header: ({ column }) => {
      return (
        <div className="flex items-center gap-1">
          <TriangleAlert size={16} />
          Missing Items
        </div>
      );
    },
    cell: ({ row }) => {
      const errors: Record<string, string[]> = row.getValue('validationErrors');

      const mapValueLabel: Record<string, string> = {
        info: 'Info',
        videoFileObject: 'Video',
        models: 'Models',
        styles: 'Styles',
        masks: 'Masks',
        printers: 'Printers',
        participants: 'Participants',
        inventory: 'Inventory',
        aiModel: 'AI Model',
        systemPrompt: 'System Prompt',
        lacesColors: 'Laces Colors',
        collarColors: 'Liner Colors',
      };

      const labels = Object.values(errors)
        .flat()
        .map((value) => mapValueLabel[value])
        .filter(Boolean);

      if (labels.length === 0) return '-';

      return (
        <div className="text-error-700">
          {labels.join(', ')}
        </div>
      );
    },
  },
];

export function EventsTable({ onClick }: EventsTableProps) {
  const { data, isFetching, refetch, ...tableProps } = useTableDataQuery<Event>(
    {
      url: '/events',
      persistState: true,
    }
  );

  return (
    <PaginatedTable<Event>
      data={data}
      onClick={onClick}
      columns={columns}
      searchable
      searchPlaceholder="Search by event name"
      emptyDescription="No event added yet"
      isLoading={isFetching}
      {...tableProps}
    />
  );
}
